/**
 * @fileoverview Read-only company profile address. Visibility flags come from the company record.
 * @module components/CompanyProfileSections/SecondTab/Location
 */
import React from "react";
import "../index.css";
import { useTranslation } from "react-i18next";
import { getCompanyFieldValue } from "../../../utils/common";

const COMMERCIAL_ADDRESS_TYPE = 1;
const EMPTY_PLACEHOLDER = "-";

const ProfileLocationItem = ({ show, label, children }) => {
  if (!show) {
    return null;
  }
  return (
    <div className="gridItem">
      <span className="gridLabel">{label}</span>
      <span className="gridValue">{children}</span>
    </div>
  );
};

const Location = ({ company, locationRefs, selectedProfile = 0 }) => {
  const { t } = useTranslation();
  const addressArray = getCompanyFieldValue(
    company,
    "direccion",
    selectedProfile,
    [],
  );
  const commercialAddress = addressArray.find(
    (address) => address.tipoDeDomicilio === COMMERCIAL_ADDRESS_TYPE,
  );
  const showStreet = Boolean(commercialAddress?.mostrarCalle);
  const showInterior = Boolean(commercialAddress?.mostrarNumeroInterior);
  const showExterior = Boolean(commercialAddress?.mostrarNumeroExterior);
  const streetLine = [
    showStreet && commercialAddress?.calle,
    showInterior &&
      commercialAddress?.numeroInterior &&
      ` ${commercialAddress.numeroInterior}`,
    showExterior &&
      commercialAddress?.numeroExterior &&
      ` ${commercialAddress.numeroExterior}`,
  ]
    .filter(Boolean)
    .join("");

  return (
    <div className="profileSection">
      <h2 className="sectionTitle">{t("pages.companyProfile.location")}</h2>
      <div className="gridContainer">
        <div className="gridRow">
          <div className="gridItem">
            {(showStreet || showInterior || showExterior) && (
              <span className="gridLabel">
                {t("pages.companyProfile.primaryAddress")}
              </span>
            )}
            <span className="gridValue completeAddress">{streetLine}</span>
          </div>
          <ProfileLocationItem
            show={commercialAddress?.mostrarCodigoPostal}
            label={t("pages.companyProfile.postalCode")}
          >
            {locationRefs?.zipCode?.codigo || EMPTY_PLACEHOLDER}
          </ProfileLocationItem>
          <ProfileLocationItem
            show={commercialAddress?.mostrarColonia}
            label={t("pages.companyProfile.neighborhood")}
          >
            {locationRefs?.neighborhood?.nombre || EMPTY_PLACEHOLDER}
          </ProfileLocationItem>
        </div>
        <div className="gridRow">
          <ProfileLocationItem
            show={commercialAddress?.mostrarEntreCalle1}
            label={t("pages.companyProfile.betweenStreet1")}
          >
            {commercialAddress?.entreCalle1}
          </ProfileLocationItem>
          <ProfileLocationItem
            show={commercialAddress?.mostrarEntreCalle2}
            label={t("pages.companyProfile.betweenStreet2")}
          >
            {commercialAddress?.entreCalle2}
          </ProfileLocationItem>
          <ProfileLocationItem
            show={commercialAddress?.mostrarMunicipio}
            label={t("pages.companyProfile.town")}
          >
            {locationRefs?.town?.nombre || EMPTY_PLACEHOLDER}
          </ProfileLocationItem>
          <ProfileLocationItem
            show={commercialAddress?.mostrarEstado}
            label={t("pages.companyProfile.state")}
          >
            {locationRefs?.state?.nombre || EMPTY_PLACEHOLDER}
          </ProfileLocationItem>
        </div>
        <div className="gridRow">
          <ProfileLocationItem
            show={commercialAddress?.mostrarPais}
            label={t("pages.companyProfile.country")}
          >
            {locationRefs?.country?.nombre || EMPTY_PLACEHOLDER}
          </ProfileLocationItem>
        </div>
      </div>
    </div>
  );
};

export default Location;
